import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Loader2, ArrowRight, KeyRound, Lock } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { ResendCode } from "@/components/ResendCode";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL_RE = /^[^@]+@[^@]+\.[^@]+$/;
const fld = "w-full rounded-xl border border-sbg-raspberry/20 bg-background px-4 py-3 text-sm outline-none focus:border-sbg-fuchsia";
const primary = "flex w-full items-center justify-center gap-2 rounded-full bg-sbg-fuchsia px-5 py-3 font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60";

export default function Login({ staff = false }) {
  usePageMeta({ title: staff ? "Staff Sign In | Southern Black Girls Toolkits" : "Sign In | Southern Black Girls Toolkits", description: staff ? "Admin and manager sign in." : "Sign in with your email to create or edit your winner page." });
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState("code");
  const [phase, setPhase] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const finish = (data) => {
    login(data.token, { email: data.email, role: data.role, is_admin: data.is_admin, is_staff: data.is_staff, toolkit_scope: data.toolkit_scope, winner_slug: data.winner_slug, device_id: data.device_id || (data.remembered ? "pending" : null) });
    toast.success(data.remembered ? "Welcome back! This device will stay signed in for 30 days." : "Welcome back!");
    if (data.is_staff) navigate("/admin");
    else if (data.winner_slug) navigate(`/bgjcpt/${data.winner_slug}`);
    else navigate("/bgjcpt/create");
  };

  const sendCode = async () => {
    const { data } = await api.post("/auth/request-code", { email });
    if (data.debug_code) toast.info(`Dev code: ${data.debug_code}`);
  };

  const requestCode = async () => {
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      await sendCode();
      setPhase("code");
      toast.success("Check your email for a 6-digit code.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send a code.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await sendCode(); toast.success("New code sent."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend the code."); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code, remember });
      finish(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code.");
    } finally { setBusy(false); }
  };

  const passwordLogin = async () => {
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-login", { email, password, remember });
      finish(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign in.");
    } finally { setBusy(false); }
  };

  const heading = mode === "password" ? "Staff sign in" : phase === "email" ? (staff ? "Staff sign in" : "Sign in to edit your page") : "Enter your code";

  const rememberBox = staff ? (
    <label data-testid="login-remember-label" className="flex cursor-pointer items-start gap-2.5 rounded-xl bg-sbg-blush/40 px-3 py-2.5 text-left">
      <input type="checkbox" data-testid="login-remember-checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-sbg-fuchsia" />
      <span className="text-xs text-sbg-burgundy/80">
        <span className="font-bold text-sbg-burgundy">Remember this device for 30 days</span>
        <span className="block text-sbg-burgundy/60">Staff accounts only. Skips codes, passwords and the inactivity sign-out on this browser.</span>
      </span>
    </label>
  ) : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="flex flex-1 items-center justify-center px-5 py-16">
        <div className="animate-pop-in w-full max-w-md rounded-3xl bg-white p-8 shadow-xl shadow-sbg-raspberry/10 ring-1 ring-sbg-raspberry/10">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sbg-raspberry text-white">
            {mode === "password" ? <Lock className="h-6 w-6" /> : phase === "email" ? <Mail className="h-6 w-6" /> : <KeyRound className="h-6 w-6" />}
          </span>
          <h1 className="mt-5 font-display text-2xl font-black text-sbg-burgundy">{heading}</h1>

          {staff && phase === "email" && (
            <div className="mt-4 inline-flex rounded-full bg-sbg-blush/50 p-1" role="tablist" data-testid="login-mode-tabs">
              {[["code", "Email me a code"], ["password", "Password"]].map(([k, label]) => (
                <button key={k} role="tab" aria-selected={mode === k} data-testid={`login-tab-${k}`} onClick={() => setMode(k)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${mode === k ? "bg-sbg-burgundy text-white" : "text-sbg-burgundy/70 hover:bg-white"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          <p className="mt-3 text-sm text-sbg-burgundy/70">
            {mode === "password"
              ? "For admins and managers who have set a password on their account."
              : phase === "email"
                ? (staff ? "Admins and managers: we'll email you a 6-digit code, or use the Password tab." : "We'll email you a 6-digit code — no password needed.")
                : `We sent a code to ${email}. It expires in 30 minutes.`}
          </p>
          {mode === "code" && phase === "code" && (
            <p data-testid="login-delivery-hint" className="mt-2 rounded-xl bg-sbg-blush/50 px-3 py-2 text-xs text-sbg-burgundy/70">
              Email can take a few minutes to arrive. Don't see it? Check your spam or junk folder and add hello@southernblackgirls.net to your contacts.
            </p>
          )}

          {mode === "password" ? (
            <div className="mt-6 space-y-4">
              <input type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={fld} autoComplete="username" />
              <input type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && passwordLogin()} placeholder="Your password" className={fld} autoComplete="current-password" />
              {rememberBox}
              <button data-testid="login-password-btn" onClick={passwordLogin} disabled={busy || !password} className={primary}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Sign in
              </button>
              <button data-testid="login-forgot-btn" onClick={() => setMode("code")} className="w-full text-center text-sm font-semibold text-sbg-raspberry hover:underline">
                Forgot your password? Email me a code instead
              </button>
            </div>
          ) : phase === "email" ? (
            <div className="mt-6 space-y-4">
              <input type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && requestCode()} placeholder="you@example.com" className={fld} />
              <button data-testid="login-request-code-btn" onClick={requestCode} disabled={busy} className={primary}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Send me a code
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <input type="text" inputMode="numeric" maxLength={6} data-testid="login-code-input" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="123456"
                className="w-full rounded-xl border border-sbg-raspberry/20 bg-background px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] outline-none focus:border-sbg-fuchsia" />
              {rememberBox}
              <button data-testid="login-verify-btn" onClick={verify} disabled={busy || code.length < 6} className={primary}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Verify &amp; sign in
              </button>
              <div className="flex flex-col items-center gap-2 text-center">
                <ResendCode onResend={resend} testId="login-resend-btn" />
                <button data-testid="login-change-email-btn" onClick={() => { setPhase("email"); setCode(""); }} className="text-sm font-semibold text-sbg-raspberry hover:underline">
                  Use a different email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
